import { produce } from 'immer';
import moment, { Moment } from 'moment';
import { AuthStore } from '../stores/auth';
import { CategoryStore } from '../stores/category';
import type { PromotionForm } from '../stores/promotion';
import {
    Promotion,
    PromotionAvailability,
    PromotionAvailabilityRequest,
    PromotionStore,
    YupPromotionForm,
} from '../stores/promotion';
import { convertLocalToUtcString } from '../utils/timezone';
import type { Navigation } from './use-navigation';
import type { PromotionTypes } from './const';

/**
 * Convert a local date to UTC ISO string (start of day)
 * @example convertDateToUtcIsoString(new Date('2024-03-15'), 'America/Toronto') // "2024-03-15T04:00:00.000Z"
 */
const convertDateToUtcIsoString = (date: Date, timezone: string): string => {
    return moment.tz(date, timezone).startOf('day').utc().toISOString();
};

/**
 * Converts local availability data for backend submission.
 * Hours are sent as-is; backend interprets them in Eastern (America/Montreal) time.
 */
export const convertAvailability = (
    availability: PromotionAvailability,
    timezone: string
): PromotionAvailabilityRequest => {
    return {
        ...(availability.id && availability.id > 0 && { id: availability.id }),
        dayOfWeek: availability.dayOfWeek,
        startHour: availability.startHour,
        endHour: availability.endHour,
        dailyLimit: availability.dailyLimit,
        startDate: convertDateToUtcIsoString(availability.startDate, timezone),
        endDate: convertDateToUtcIsoString(availability.endDate, timezone),
    };
};

/**
 * Converts availability data from backend for display/editing.
 * Hours are used as-is; backend stores them in Eastern (America/Montreal) time.
 */
export const convertAvailabilityFromApi = (
    apiAvailability: PromotionAvailabilityRequest,
    timezone: string
): PromotionAvailability => {
    return {
        ...(apiAvailability.id && { id: apiAvailability.id }),
        dayOfWeek: apiAvailability.dayOfWeek,
        startHour: apiAvailability.startHour,
        endHour: apiAvailability.endHour,
        dailyLimit: apiAvailability.dailyLimit,
        startDate: moment.tz(apiAvailability.startDate, timezone).toDate(),
        endDate: moment.tz(apiAvailability.endDate, timezone).toDate(),
    };
};

const inverseConverter = (promo: PromotionForm): unknown => {
    const toUtcStringOrNull = (date: Moment | null): string | null =>
        date && promo.timezone
            ? convertLocalToUtcString(date, promo.timezone)
            : null;

    // dates are Moment objects here
    return {
        ...promo,
        // Convert moments to UTC strings, or null if invalid
        startDate: toUtcStringOrNull(promo.startDate),
        endDate: toUtcStringOrNull(promo.endDate),
        activationReadyDate: toUtcStringOrNull(promo.activationReadyDate),
        deactivationDisplayDate: toUtcStringOrNull(
            promo.deactivationDisplayDate
        ),
        // Convert local availability data to UTC for backend submission
        availabilities:
            promo.availabilities && promo.timezone
                ? promo.availabilities.map((avail) =>
                      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                      convertAvailability(avail, promo.timezone!)
                  )
                : null,
        // description is already an HTML string, no conversion needed
        items: promo.items,
    };
};

export const createPromotion = async (
    formik: { values: PromotionForm },
    promotionStore: PromotionStore,
    navigation: Navigation,
    authStore: AuthStore,
    clearDirty?: () => void,
    options?: {
        onCreated?: (id: number) => Promise<boolean | void> | boolean | void;
        onRedirect?: () => void;
    }
) => {
    formik.values.cmsUserEmail = authStore.claims?.email || null;

    const unknownPromo = inverseConverter(formik.values);
    if (!YupPromotionForm.isValidSync(unknownPromo)) {
        throw new Error('Promotion validation failed');
    }
    const createdId = await promotionStore.createPromotion(
        (unknownPromo as unknown) as Promotion
    );
    if (typeof createdId === 'number') {
        const shouldContinue = await options?.onCreated?.(createdId);
        if (shouldContinue === false) {
            return createdId ?? null;
        }
    }
    clearDirty?.();
    if (options?.onRedirect) {
        options.onRedirect();
    } else {
        formik.values.type === 'coupon'
            ? navigation.replaceToCoupons()
            : navigation.replaceToCouponsInStore();
    }
    return createdId ?? null;
};

export const updatePromotion = async (
    formik: { values: PromotionForm },
    promotionStore: PromotionStore,
    navigation: Navigation,
    authStore: AuthStore,
    clearDirty?: () => void
) => {
    formik.values.cmsUserEmail = authStore.claims?.email || null;

    const unknownPromo = inverseConverter(formik.values);
    if (!YupPromotionForm.isValidSync(unknownPromo)) {
        throw new Error('Promotion validation failed');
    }
    await promotionStore.modifyPromotion(
        (unknownPromo as unknown) as Promotion
    );
    clearDirty?.();
    formik.values.type === 'coupon'
        ? navigation.replaceToCoupons()
        : navigation.replaceToCouponsInStore();
};

export const setCategory = (
    categoryId: number,
    formik: {
        values: PromotionForm;
        setFieldTouched: (field: string, isTouched: boolean) => void;
        setValues: (values: PromotionForm) => void;
    },
    categoryStore: CategoryStore
) => {
    const values = produce(formik.values, (draft) => {
        draft.categoryId = categoryId;
        draft.category = categoryStore.getActiveCategoryById(categoryId);
    });
    formik.setFieldTouched('categoryId', true);
    formik.setValues(values);
};

export const onDeleteConfirm = async (
    params: { id: string; type: PromotionTypes },
    setIsDeleting: (isDeleting: boolean) => void,
    promotionStore: PromotionStore,
    navigation: Navigation
) => {
    setIsDeleting(true);
    const response = await promotionStore.delete(Number(params.id));
    setIsDeleting(false);

    if (!response.err) {
        params.type === 'coupon'
            ? navigation.replaceToCoupons()
            : navigation.replaceToCouponsInStore();
    }
};
